import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './onboarding.css'

export type TourAnchorPlacement = 'right' | 'left' | 'bottom' | 'bottom-end' | 'top'

type OnboardingAnchorProps = {
  active: boolean
  target: string | null
  placement?: string
  children: React.ReactNode
}

type Coords = { top: number; left: number; placement: TourAnchorPlacement }

const GAP = 16
const VIEW_PAD = 12

const getHtmlZoom = () => {
  const style = getComputedStyle(document.documentElement) as CSSStyleDeclaration & {
    zoom?: string
  }
  const raw = String(style.zoom || '1')
  const n = raw.endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw)
  return Number.isFinite(n) && n > 0 ? n : 1
}

const normalizePlacement = (value?: string): TourAnchorPlacement => {
  if (value === 'bottom-end') return 'bottom-end'
  if (value?.startsWith('bottom')) return 'bottom'
  if (value?.startsWith('left')) return 'left'
  if (value?.startsWith('top')) return 'top'
  return 'right'
}

const computeCoords = (
  target: Element,
  tip: HTMLElement,
  placement: TourAnchorPlacement
): Coords => {
  const zoom = getHtmlZoom()
  const rect = target.getBoundingClientRect()
  const tipW = tip.offsetWidth || 380
  const tipH = tip.offsetHeight || 260
  const viewW = document.documentElement.clientWidth
  const viewH = document.documentElement.clientHeight

  const topLayout = rect.top / zoom
  const leftLayout = rect.left / zoom
  const widthLayout = rect.width / zoom
  const heightLayout = rect.height / zoom
  const rightLayout = leftLayout + widthLayout
  const bottomLayout = topLayout + heightLayout

  let left = rightLayout + GAP
  let top = topLayout + heightLayout / 2 - tipH / 2
  let nextPlacement = placement

  if (placement === 'left') {
    left = leftLayout - GAP - tipW
    top = topLayout + heightLayout / 2 - tipH / 2
    nextPlacement = 'left'
    if (left < VIEW_PAD) {
      left = rightLayout + GAP
      nextPlacement = 'right'
    }
  } else if (placement === 'right') {
    if (left + tipW > viewW - VIEW_PAD && leftLayout - GAP - tipW > VIEW_PAD) {
      left = leftLayout - GAP - tipW
      nextPlacement = 'left'
    }
  } else if (placement === 'bottom-end') {
    left = rightLayout - tipW
    top = bottomLayout + GAP
    nextPlacement = 'bottom-end'
  } else if (placement === 'top') {
    left = leftLayout + widthLayout / 2 - tipW / 2
    top = topLayout - GAP - tipH
    nextPlacement = 'top'
    if (top < VIEW_PAD) {
      top = bottomLayout + GAP
      nextPlacement = 'bottom'
    }
  } else {
    left = leftLayout + widthLayout / 2 - tipW / 2
    top = bottomLayout + GAP
    nextPlacement = 'bottom'
  }

  left = Math.min(Math.max(VIEW_PAD, left), Math.max(VIEW_PAD, viewW - tipW - VIEW_PAD))
  top = Math.min(Math.max(VIEW_PAD, top), Math.max(VIEW_PAD, viewH - tipH - VIEW_PAD))

  return { top, left, placement: nextPlacement }
}

export const OnboardingAnchor = ({
  active,
  target,
  placement,
  children,
}: OnboardingAnchorProps) => {
  const tipRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<Coords | null>(null)
  const resolvedPlacement = normalizePlacement(placement)

  useLayoutEffect(() => {
    if (!active || !target) {
      setCoords(null)
      return
    }

    const update = () => {
      const el = document.querySelector(target)
      const tip = tipRef.current
      if (!el || !tip) return
      setCoords(computeCoords(el, tip, resolvedPlacement))
    }

    const el = document.querySelector(target)
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }

    const frame = window.requestAnimationFrame(update)
    const intervalId = window.setInterval(update, 80)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)

    return () => {
      window.cancelAnimationFrame(frame)
      window.clearInterval(intervalId)
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [active, target, resolvedPlacement, children])

  if (!active || !target) return null

  return createPortal(
    <div
      ref={tipRef}
      className={`ob-anchor-tip ob-anchor-tip--${coords?.placement || resolvedPlacement}`}
      style={
        coords
          ? {
              top: coords.top,
              left: coords.left,
              visibility: 'visible',
            }
          : {
              top: 0,
              left: 0,
              visibility: 'hidden',
            }
      }
    >
      {children}
    </div>,
    document.body
  )
}
